import { isValid, parseISO } from "date-fns";

import { apiRequest } from "@/lib/api/http";
import { costNullableNumber, costNullableText, costNumber, costText } from "@/lib/api/cost-fields";
import type { ParsedVehicleEvent } from "@/lib/notifications/vehicle-events";
import type { VehicleNotification } from "@/types/fleet";

/**
 * Upstream's stored vehicle events (`ClientNotification.php`, `docs/CLIENT_NOTIFICATIONS_HANDOFF.md`).
 *
 * The socket still raises the toasts, but the notifications page and the bell count read this:
 * it holds everything that happened, including while the app was closed. Upstream scopes it to the
 * user's groups, so nothing is filtered by vehicle ownership here.
 */

export const NOTIFICATIONS_PAGE_SIZE = 100;

interface RemoteClientNotification {
  id: string;
  client_device_id?: string;
  event_type?: string;
  zone_id?: string | null;
  driver_ref?: string | null;
  prev_driver_ref?: string | null;
  x?: string | null;
  y?: string | null;
  speed?: string | null;
  heading?: string | null;
  speed_limit?: string | null;
  value?: string | null;
  label?: string;
  detail?: {
    registration?: string;
    zone_title?: string;
    driver_name?: string;
    prev_driver_name?: string;
  } | null;
  occurred_at?: string;
  created_at?: string;
}

interface RemoteNotificationList {
  success: true;
  total?: string;
  unread?: string;
  seen_at?: string | null;
  data: RemoteClientNotification[];
}

interface RemoteSeenResponse {
  success: true;
  seen_at?: string;
}

/**
 * Upstream stamps are `YYYY-MM-DD HH:MM:SS` with no zone marker, but they are UTC (the server's
 * clock). Reading them as browser-local shifted every row by the viewer's offset. A stamp that
 * already carries a zone is taken as given; `""` when missing or malformed.
 */
export function parseServerUtcTime(value: string | null | undefined): string {
  const text = value?.trim() ?? "";
  if (!text) return "";
  const iso = /^\d{4}-\d{2}-\d{2}[ T]\d{2}:\d{2}(:\d{2})?$/.test(text) ? `${text.replace(" ", "T")}Z` : text;
  const date = parseISO(iso);
  return isValid(date) ? date.toISOString() : "";
}

function optionalText(value: string | undefined): string | undefined {
  const text = value?.trim();
  return text ? text : undefined;
}

export function mapNotification(row: RemoteClientNotification): VehicleNotification {
  const lat = costNullableNumber(row.x);
  const lon = costNullableNumber(row.y);
  // `0,0` is a device without a fix, as everywhere else on the map.
  const hasPosition = lat !== null && lon !== null && !(lat === 0 && lon === 0);
  return {
    id: row.id,
    vehicleId: costText(row.client_device_id),
    eventType: costText(row.event_type),
    zoneId: costNullableText(row.zone_id),
    driverRef: costNullableText(row.driver_ref),
    prevDriverRef: costNullableText(row.prev_driver_ref),
    lat: hasPosition ? lat : null,
    lon: hasPosition ? lon : null,
    speed: costNullableNumber(row.speed),
    heading: costNullableNumber(row.heading),
    speedLimit: costNullableText(row.speed_limit),
    value: costNullableText(row.value),
    label: costText(row.label),
    detail: {
      registration: optionalText(row.detail?.registration),
      zoneTitle: optionalText(row.detail?.zone_title),
      driverName: optionalText(row.detail?.driver_name),
      prevDriverName: optionalText(row.detail?.prev_driver_name),
    },
    occurredAt: parseServerUtcTime(row.occurred_at),
    createdAt: parseServerUtcTime(row.created_at),
  };
}

const ZONE_EVENT_PREFIXES = ["parking_", "go_zone_", "nogo_zone_"];

/**
 * The socket form of a stored event (`type` + the value after its first colon), so the toast's
 * describers, icons and severities apply to history rows unchanged.
 */
export function toParsedEvent(notification: VehicleNotification): ParsedVehicleEvent {
  const type = notification.eventType;
  let arg: string | null = null;
  if (ZONE_EVENT_PREFIXES.some((prefix) => type.startsWith(prefix))) arg = notification.zoneId;
  else if (type.startsWith("driver_")) arg = notification.driverRef;
  else if (type.includes("speeding_")) arg = notification.speedLimit;
  else if (type === "refuel" || type === "missing_fuel" || type === "overheat") arg = notification.value;
  return { type, arg };
}

export interface NotificationFilters {
  /** `YYYY-MM-DD`, inclusive, on when the event happened. */
  dateFrom?: string;
  dateTo?: string;
  vehicleIds?: string[];
  eventTypes?: string[];
  limit?: number;
  offset?: number;
}

/** Only the keys the proxy handler allows through. */
export function notificationFiltersToQuery(filters: NotificationFilters): string {
  const params = new URLSearchParams();
  if (filters.dateFrom) params.set("date_from", filters.dateFrom);
  if (filters.dateTo) params.set("date_to", filters.dateTo);
  if (filters.vehicleIds?.length) params.set("device_id", filters.vehicleIds.join(","));
  if (filters.eventTypes?.length) params.set("event_type", filters.eventTypes.join(","));
  params.set("limit", String(filters.limit ?? NOTIFICATIONS_PAGE_SIZE));
  if (filters.offset) params.set("offset", String(filters.offset));
  return `?${params.toString()}`;
}

export interface NotificationPage {
  items: VehicleNotification[];
  /** Rows matching the filters upstream, for paging. */
  total: number;
  /** The user's unread count. Ignores the filters, so it is the bell's number. */
  unread: number;
  /** ISO; `""` when the user never marked anything seen. */
  seenAt: string;
}

export async function listNotifications(filters: NotificationFilters = {}): Promise<NotificationPage> {
  const response = await apiRequest<RemoteNotificationList>(
    `/api/notifications${notificationFiltersToQuery(filters)}`,
    { method: "GET", cache: "no-store" },
  );
  const items = (response.data ?? []).map(mapNotification);
  return {
    items,
    total: response.total === undefined ? items.length : costNumber(response.total),
    unread: costNumber(response.unread),
    seenAt: parseServerUtcTime(response.seen_at),
  };
}

/** Marks everything up to now as seen, for this user on every device. Returns the new `seen_at`. */
export async function markNotificationsSeen(): Promise<string> {
  const response = await apiRequest<RemoteSeenResponse>("/api/notifications", { method: "POST" });
  return parseServerUtcTime(response.seen_at);
}

/** Unread when written after `seenAt`, or when nothing was ever marked seen. */
export function isNotificationUnread(notification: VehicleNotification, seenAt: string): boolean {
  if (!seenAt) return true;
  return notification.createdAt > seenAt;
}
